import React, { useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileJson, FileSpreadsheet, ArrowLeft, CheckCircle2, XCircle, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const SAMPLE = `{
  "questions": [
    {
      "exam_type": "LGS", "year": 2024, "grade_level": "8",
      "subject": "Matematik", "topic": "Cebir", "difficulty": "medium",
      "text": "3x + 5 = 20 denkleminde x kaçtır?",
      "options": [
        {"key": "A", "text": "3"},
        {"key": "B", "text": "5"},
        {"key": "C", "text": "7"},
        {"key": "D", "text": "10"}
      ],
      "correct_option": "B",
      "explanation": "3x = 15 → x = 5"
    }
  ]
}`;

export default function AdminQuestionImport() {
  const [json, setJson] = useState(SAMPLE);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submitJson = async () => {
    setBusy(true); setResult(null);
    try {
      const parsed = JSON.parse(json);
      const { data } = await api.post(`${V1}/admin/questions/bulk-import/json`, {
        questions: parsed.questions || parsed,
      });
      setResult(data);
      if (data.created > 0) toast.success(`${data.created} soru içe aktarıldı`);
      if ((data.errors || []).length) toast.warning(`${data.errors.length} satırda hata var`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`${V1}/admin/questions/bulk-import/template.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "sanostudent-soru-sablonu.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şablon indirilemedi");
    }
  };

  const submitFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`${V1}/admin/questions/bulk-import/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) toast.success(`${data.created} soru içe aktarıldı`);
      if ((data.errors || []).length) toast.warning(`${data.errors.length} satırda hata var`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dosya yüklenemedi");
    } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid="admin-question-import-page">
      <div className="flex items-center gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm"><Link to="/admin/sorular"><ArrowLeft className="h-4 w-4" /></Link></Button>
          <div>
            <div className="overline text-accent">Soru Bankası</div>
            <h1 className="font-display text-3xl font-bold">Toplu Soru İçe Aktarma</h1>
          </div>
        </div>
        <Button onClick={downloadTemplate} variant="outline" data-testid="download-xlsx-template-btn">
          <Download className="h-4 w-4" /> Excel Şablonunu İndir
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <div className="font-display font-bold text-lg">Dosya Yükle</div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            .xlsx, .csv veya .json formatında dosya yükleyin. Şablon için sağdaki JSON örneğini kullanın.
            CSV başlıkları: <code className="text-xs bg-muted px-1 rounded">exam_type,year,grade_level,subject,topic,difficulty,text,option_A,option_B,option_C,option_D,option_E,correct_option,explanation</code>
          </p>
          <label className="block">
            <input type="file" accept=".xlsx,.csv,.json" onChange={submitFile} disabled={busy} className="hidden" data-testid="file-input" id="bulk-import-file" />
            <span className="inline-flex items-center justify-center gap-2 w-full h-24 rounded-lg border-2 border-dashed border-border cursor-pointer hover:border-primary/40" style={{ transition: "border-color 0.2s" }}>
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{busy ? "Yükleniyor…" : "Tıklayıp dosya seçin"}</span>
            </span>
          </label>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileJson className="h-5 w-5 text-primary" />
            <div className="font-display font-bold text-lg">JSON ile Yapıştır</div>
          </div>
          <Textarea
            rows={16}
            className="font-mono text-xs"
            value={json}
            onChange={(e) => setJson(e.target.value)}
            data-testid="json-input"
          />
          <Button onClick={submitJson} disabled={busy} className="mt-3 w-full" data-testid="submit-json-btn">
            {busy ? "İçe aktarılıyor…" : "JSON'u İçe Aktar"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="import-result">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <ResultStat icon={CheckCircle2} tone="success" label="Eklendi" value={result.created} />
            <ResultStat icon={XCircle} tone="destructive" label="Hata" value={(result.errors || []).length} />
            <ResultStat icon={Upload} tone="muted" label="Toplam Gönderilen" value={result.total_submitted} />
          </div>
          {(result.errors || []).length > 0 && (
            <div>
              <div className="font-display font-bold text-sm mb-2">Hatalar</div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-sm p-2 bg-destructive/5 border-l-2 border-destructive rounded">
                    <b>Satır {e.row}:</b> {e.error} <span className="text-xs text-muted-foreground">— {e.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ResultStat = ({ icon: Icon, tone, label, value }) => {
  const map = { success: "text-success bg-success/10", destructive: "text-destructive bg-destructive/10", muted: "text-muted-foreground bg-muted" };
  return (
    <div className="p-4 rounded-lg border border-border">
      <div className={`inline-flex h-8 w-8 items-center justify-center rounded ${map[tone]}`}><Icon className="h-4 w-4" /></div>
      <div className="text-2xl font-display font-bold mt-2">{value}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
};
